import parseZone from 'helpers/parseZone';
import { useBreakpoint } from 'components/BreakpointProvider';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';
import { FlexCenterBoth } from 'components/primitives/flex';
import {
  useAcceptBookingModificationMutation,
  useDeclineBookingModificationMutation,
} from 'components/booking/mutationHooks';
import useModal from 'components/remodal/v2/useModal';
import ConfirmEditBookingModal from 'components/modals/ConfirmEditBookingModal';
import { ButtonVariant } from 'components/ui/BookingActionsBox/types';
import useLazySnackbar, { EnqueueSnackbarLazyType } from 'components/banners/useLazySnackbar';
import BookingActionsBox from 'components/ui/BookingActionsBox/BookingActionsBox';
import { Loader } from '../commons';
import { SpecialMessageProps } from './types';
import DefaultMessage from './DefaultMessage';

export default function ModificationMessage({
  msg,
  lastEdits,
  index,
  booking,
  userType,
  conversation,
}: SpecialMessageProps) {
  const { enqueueSnackbar } = useLazySnackbar();
  const queryClient = useQueryClient();
  const breakpoints = useBreakpoint();
  const smallScreen = breakpoints.xs || breakpoints.sm;

  const bookingModification = msg?.booking_modification;
  const hasBookingModifications = !!bookingModification;

  const onModificationSuccess = () => {
    openConfirmEditModal();
  };

  const acceptModificationMutation = useAcceptBookingModificationMutation({
    onSuccess: onModificationSuccess,
    onError: handleError(enqueueSnackbar),
  });

  const declineModificationMutation = useDeclineBookingModificationMutation({
    onSuccess: onModificationSuccess,
    onError: handleError(enqueueSnackbar),
  });

  const { modal: confirmEditModal, openModal: openConfirmEditModal } = useModal(
    <ConfirmEditBookingModal
      isApproveAction={acceptModificationMutation.isSuccess}
      booking={booking}
      bookingModification={bookingModification}
      targetUserName={
        conversation.my_user_type_in_conversation === 'renter' ? booking.owner.first_name : booking.renter.first_name
      }
      onCloseModal={() => {
        queryClient.invalidateQueries(['conversationDetail', msg.conversation_id]);
      }}
    />,
    {
      size: 'medium',
      trackingName: 'confirm_edit_booking_modal',
    }
  );

  const isOwner = userType === 'owner';
  if (
    (isOwner ? lastEdits.bookingRenter : lastEdits.bookingOwner) !== index ||
    !hasBookingModifications ||
    bookingModification?.state !== 'submitted'
  ) {
    return <DefaultMessage message={msg} />;
  }

  const expiresAt = parseZone(bookingModification?.expires_at).format('MMM DD, YYYY • h:mm A');

  return (
    <Container>
      {(acceptModificationMutation.isLoading || declineModificationMutation.isLoading) && (
        <LoaderWrapper>
          <Loader />
        </LoaderWrapper>
      )}

      <BookingActionsBox
        title={<span>Edits requested</span>}
        ctaText={smallScreen ? 'Review edits' : 'Approve edits'}
        subHeading={getEditBookingMessage(
          bookingModification,
          booking?.[isOwner ? 'renter' : 'owner']?.first_name,
          booking
        )}
        userType={userType as ButtonVariant}
        onSubmit={() => {
          if (smallScreen) {
            // @ts-ignore
            window.location = booking.details_url;
          } else {
            acceptModificationMutation.mutate({
              bookingId: booking.id,
              modificationId: msg.mod_id,
            });
          }
        }}
        showCloseButton={false}
        showSubtitle
        subtitle={`This edit request will expire on ${expiresAt}.`}
        ctas={
          smallScreen
            ? []
            : [
                {
                  ctaText: 'Decline edits',
                  userType: `${isOwner ? 'owner' : 'renter'}_outline`,
                  onSubmit: () => {
                    declineModificationMutation.mutate({
                      bookingId: booking.id,
                      modificationId: msg.mod_id,
                    });
                  },
                },
              ]
        }
      />

      {confirmEditModal}
    </Container>
  );
}

const Container = styled.div`
  position: relative;
`;

const LoaderWrapper = styled(FlexCenterBoth)`
  position: absolute;
  inset: 0;
  z-index: 1;
  backdrop-filter: brightness(80%);
  margin: 0 40px 40px;
  border-radius: 16px;
`;

const getEditBookingMessage = (bookingModifications, first_name, booking) => {
  const prevStart = parseZone(booking?.trip_start);
  const start = parseZone(bookingModifications?.trip_start);

  const timeChange =
    prevStart.format('h:mm A') !== start.format('h:mm A') ? `start time to ${start.format('h:mm A')}` : null;
  const dateChange = prevStart.diff(start, 'day') !== 0 ? `start date to ${start.format('MMM D, YYYY')}` : null;

  return `${first_name} has requested to edit the ${[dateChange, timeChange].filter(Boolean).join(' and/or ')}`;
};

const handleError = (enqueueSnackbar: EnqueueSnackbarLazyType) => (error) => {
  if (error?.error_type) {
    enqueueSnackbar(error?.error_message, { variant: 'error' });
  } else {
    enqueueSnackbar(error, { variant: 'error' });
  }
};
